// src/oura/components/docs/write/TestClientEmailDialog.tsx
//
// Ask before a test of the client email goes (#4844), as SendClientEmailDialog
// asks before the real one: the subject, where it goes (ourteam's own test
// inbox, which the server picks), and that nothing reaches the client. Same
// shell: a portal on <body>, Cancel first, Escape cancels, no dismiss while
// the test is on its way. A refusal keeps the dialog open with the reason.

import { createPortal } from "react-dom";
import { FlaskConicalIcon, Loader2Icon } from "lucide-react";
import { Button } from "../../../primitives/Button";

export function TestClientEmailDialog({
    open,
    docId,
    subject,
    sending,
    failed,
    onSend,
    onClose,
}: {
    open: boolean;
    docId: string;
    subject: string;
    sending: boolean;
    failed: string | null;
    onSend: () => void;
    onClose: () => void;
}) {
    if (!open) return null;
    const cancel = () => !sending && onClose();
    const titleId = `test-client-email-title-${docId}`;

    return createPortal(
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            data-test-client-email-dialog={docId}
            // A portal still bubbles through React to the drawer: keep clicks and keys here.
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Escape") cancel();
            }}
        >
            <div className="absolute inset-0 bg-gray-900/50" onClick={cancel} />

            <div className="relative w-full max-w-md rounded-lg bg-white shadow-xl">
                <div className="flex items-start gap-3 px-6 pt-5">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-gray-100">
                        <FlaskConicalIcon className="h-5 w-5 text-gray-600" />
                    </div>
                    <div className="min-w-0 pt-1.5">
                        <h2 id={titleId} className="text-base font-semibold text-gray-900">
                            Send a test of this email?
                        </h2>
                    </div>
                </div>

                <div className="space-y-3 px-6 pb-1 pt-3">
                    <p className="m-0 break-words text-sm text-gray-700">
                        <span className="text-gray-500">Subject: </span>
                        {subject}
                    </p>
                    <p className="m-0 text-sm leading-relaxed text-gray-600">
                        It goes as it stands to ourteam&rsquo;s own test inbox only. Nothing goes to the client, and nothing is kept on the doc.
                    </p>
                    {failed && (
                        <p role="alert" className="m-0 text-sm text-rose-600">
                            {failed}
                        </p>
                    )}
                </div>

                <div className="flex gap-2 px-6 pb-5 pt-4">
                    {/* Focus starts on Cancel, never on the button that sends. */}
                    <Button variant="outline" className="flex-1" disabled={sending} onClick={cancel} autoFocus>
                        Cancel
                    </Button>
                    <Button className="flex-1" disabled={sending} onClick={onSend} data-test-client-email-confirm={docId}>
                        {sending ? (
                            <>
                                <Loader2Icon className="h-4 w-4 animate-spin" />
                                Sending…
                            </>
                        ) : (
                            "Send the test"
                        )}
                    </Button>
                </div>
            </div>
        </div>,
        document.body,
    );
}
